import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { FileCheck, Mail, MessageCircle, RefreshCw, Image, Trash2, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { bankChip, bankLabel } from '../utils/requestTheme';
import { ExportMenu } from '../components/ExportMenu';


export default function UploadBukti() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bank-process/upload-bukti`);
      setItems(res.data);
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadUrl.trim()) {
      toast.error('URL bukti transaksi wajib diisi');
      return;
    }
    
    setUploading(true);
    try {
      await api.post(`/bank-process/${selectedItem.id}/upload-bukti?bukti_url=${encodeURIComponent(uploadUrl)}`);
      toast.success('Bukti transaksi berhasil diupload!');
      setShowUpload(false);
      setUploadUrl('');
      setSelectedItem(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal upload bukti');
    } finally {
      setUploading(false);
    }
  };

  const handleSendNotification = (item, type) => {
    // Placeholder for email/WA notification
    toast.info(`Fitur kirim ${type === 'email' ? 'Email' : 'WhatsApp'} akan segera tersedia`);
  };

  // Admin delete functions
  const openDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async (reason) => {
    if (!itemToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/transactions/bank_process/${itemToDelete.id}`, {
        data: { reason }
      });
      toast.success(`${itemToDelete.request_number} berhasil dihapus`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID').format(value || 0);
  };

  const exportedItems = items.filter(i => i.status === 'exported');
  const uploadedItems = items.filter(i => i.status === 'uploaded');

  return (
    <Layout>
      <div className="space-y-6" data-testid="upload-bukti-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Upload Bukti Transaksi</h1>
            <p className="text-slate-500 mt-1">Upload bukti pembayaran dan kirim notifikasi</p>
          </div>
          <div className="flex gap-2">
            <ExportMenu table="upload-bukti" size="default" />
            <Button variant="outline" onClick={fetchItems} data-testid="refresh-upload">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Menunggu Upload</p>
                  <p className="text-2xl font-bold">{exportedItems.length}</p>
                </div>
                <Download className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Sudah Upload</p>
                  <p className="text-2xl font-bold">{uploadedItems.length}</p>
                </div>
                <FileCheck className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Transaksi</p>
                  <p className="text-2xl font-bold">Rp {formatCurrency(items.reduce((sum, i) => sum + (i.nominal_transaksi || 0), 0))}</p>
                </div>
                <span className="text-3xl">ðŸ’°</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Menunggu Upload */}
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-amber-600" />
              Menunggu Upload Bukti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : exportedItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-2 font-semibold">No. Request</th>
                      <th className="px-4 py-2 font-semibold">Bank</th>
                      <th className="px-4 py-2 font-semibold">Penerima</th>
                      <th className="px-4 py-2 font-semibold">Rekening</th>
                      <th className="px-4 py-2 font-semibold text-right">Nominal</th>
                      <th className="px-4 py-2 font-semibold">Exported</th>
                      <th className="px-4 py-2 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exportedItems.map((item) => (
                      <tr key={item.id} className="border-b" data-testid={`exported-item-${item.id}`}>
                        <td className="px-4 py-2 font-mono font-semibold">{item.request_number}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${bankChip(item.bank_type)}`}>
                            {bankLabel(item.bank_type)}
                          </span>
                        </td>
                        <td className="px-4 py-2">{item.penerima_nama}</td>
                        <td className="px-4 py-2 font-mono text-xs">{item.penerima_rekening}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">
                          Rp {formatCurrency(item.nominal_transaksi)}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">
                          {item.exported_at && new Date(item.exported_at).toLocaleString('id-ID')}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700"
                              onClick={() => { setSelectedItem(item); setShowUpload(true); }}
                              data-testid={`upload-btn-${item.id}`}
                            >
                              <Download className="h-4 w-4 mr-1" /> Upload
                            </Button>
                            {user?.role === 'admin' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => openDeleteDialog(item)}
                                data-testid={`delete-exported-${item.id}`}
                                title="Hapus"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Tidak ada transaksi yang menunggu upload</p>
            )}
          </CardContent>
        </Card>

        {/* Sudah Upload */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-emerald-600" />
              Sudah Upload Bukti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {uploadedItems.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-2 font-semibold">No. Request</th>
                      <th className="px-4 py-2 font-semibold">Bank</th>
                      <th className="px-4 py-2 font-semibold">Penerima</th>
                      <th className="px-4 py-2 font-semibold text-right">Nominal</th>
                      <th className="px-4 py-2 font-semibold">Bukti</th>
                      <th className="px-4 py-2 font-semibold">Uploaded</th>
                      <th className="px-4 py-2 font-semibold">Kirim Notifikasi</th>
                      {user?.role === 'admin' && <th className="px-4 py-2 font-semibold">Hapus</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedItems.map((item) => (
                      <tr key={item.id} className="border-b" data-testid={`uploaded-item-${item.id}`}>
                        <td className="px-4 py-2 font-mono font-semibold">{item.request_number}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${bankChip(item.bank_type)}`}>
                            {bankLabel(item.bank_type)}
                          </span>
                        </td>
                        <td className="px-4 py-2">{item.penerima_nama}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold">
                          Rp {formatCurrency(item.nominal_transaksi)}
                        </td>
                        <td className="px-4 py-2">
                          {item.bukti_transaksi_url ? (
                            <a
                              href={item.bukti_transaksi_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-blue-600 hover:underline"
                            >
                              <Image className="h-4 w-4" /> Lihat
                            </a>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">
                          {item.bukti_uploaded_at && new Date(item.bukti_uploaded_at).toLocaleString('id-ID')}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendNotification(item, 'email')}
                              data-testid={`send-email-${item.id}`}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 border-emerald-300"
                              onClick={() => handleSendNotification(item, 'wa')}
                              data-testid={`send-wa-${item.id}`}
                            >
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                        {user?.role === 'admin' && (
                          <td className="px-4 py-2">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => openDeleteDialog(item)}
                              data-testid={`delete-uploaded-${item.id}`}
                              title="Hapus"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Belum ada bukti yang diupload</p>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={showUpload} onOpenChange={setShowUpload}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Bukti Transaksi</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-slate-500">No. Request</p>
                      <p className="font-mono font-semibold">{selectedItem.request_number}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Bank</p>
                      <p className="font-semibold">{bankLabel(selectedItem.bank_type)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Penerima</p>
                      <p className="font-semibold">{selectedItem.penerima_nama}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Nominal</p>
                      <p className="font-mono font-semibold">Rp {formatCurrency(selectedItem.nominal_transaksi)}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bukti-url">URL Bukti Transaksi *</Label>
                  <Input
                    id="bukti-url"
                    placeholder="https://... (link gambar/PDF bukti transfer)"
                    value={uploadUrl}
                    onChange={(e) => setUploadUrl(e.target.value)}
                    data-testid="bukti-url-input"
                  />
                  <p className="text-xs text-slate-500">
                    Upload gambar ke cloud storage (Google Drive, Dropbox, dll) lalu paste link-nya di sini
                  </p>
                </div>

                <Button
                  onClick={handleUpload}
                  disabled={uploading || !uploadUrl.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Upload Bukti
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDelete}
          title="Hapus Data Transaksi"
          description="Apakah Anda yakin ingin menghapus data ini? Tindakan ini tidak dapat dibatalkan dan akan dicatat dalam log penghapusan."
          itemName={itemToDelete ? `${itemToDelete.request_number} - ${itemToDelete.penerima_nama} - Rp ${formatCurrency(itemToDelete.nominal_transaksi)}` : ''}
          loading={deleteLoading}
        />
      </div>
    </Layout>
  );
}



